import React, { useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { IconFolder, IconBack, IconRefresh, IconZoom } from "icons";
import { FilterSearch } from "components/FilterBar";
import { SleekToggle } from "components/Sleek";
import Button from "components/Button";
import ImagePopup from "components/ImagePopup";
import { IBrowzwearBoard, IBrowzwearFolder } from "api/browzwear/browzwear.type";
import BrowzwearAPIForm, { BrowzwearCredentials } from "components/BrowzwearAPIForm";
import { useBrowzwearBoards } from "hooks/useBrowzwearBoards";
import { useAppSelector } from "store";
import { previewUrl } from "utils/previewUrl";
import { useIntegrationCardFooter } from "./IntegrationCard";
import { PAGE_SIZE, boardImageUrls, filterFolders, folderImageUrls, isCollection, pageCount, pageOf, sortCollectionsFirst } from "./browzwearTree";

const GRID = "grid grid-cols-4 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3";

/** Prev / next over an in-memory list. Nothing is paginated server-side yet — the whole tree arrives
 * in one `/import` payload — so this bounds what is RENDERED, which is what made a 128-board /
 * 1146-image store unusable (REF-6498). */
const Pager = ({ page, total, onChange }: { page: number; total: number; onChange: (page: number) => void }) => {
  const { t } = useTranslation();

  if (total <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-3">
      <Button className="btn-secondary-small" onClick={() => onChange(page - 1)} disabled={page <= 1}>
        {t("assets:assets.integrations.prev_page")}
      </Button>
      <span className="text-grey text-bodySm">{t("assets:assets.integrations.page_of", { page, total })}</span>
      <Button className="btn-secondary-small" onClick={() => onChange(page + 1)} disabled={page >= total}>
        {t("assets:assets.integrations.next_page")}
      </Button>
    </div>
  );
};

/** Four thumbnails + a name. Shared by the folder and the board level — they render identically,
 * only what they count differs. `loading="lazy"` is not cosmetic here: a folder card renders 4
 * `<img>` each, so a full grid used to fetch 4 × board-count images from Stylezone on first paint. */
const TreeCard = ({ name, badge, subtitle, thumbnails, onClick }: { name: string; badge?: string; subtitle?: string; thumbnails: string[]; onClick: () => void }) => (
  <div className="flex flex-col gap-2 border border-black-300 rounded-md p-3 cursor-pointer hover:border-grey transition-colors" onClick={onClick}>
    <div className="flex items-center gap-[5px]">
      <IconFolder className="w-4 h-4 shrink-0 text-trueBlack dark:text-white" />
      <span className="text-head7 text-trueBlack dark:text-white truncate">{name}</span>
      {badge && <span className="shrink-0 px-1.5 py-0.5 rounded bg-purpleGreen/20 text-purpleGreen text-bodyXs">{badge}</span>}
    </div>
    {subtitle && <span className="text-grey text-bodyXs -mt-1 truncate">{subtitle}</span>}
    <div className="grid grid-cols-2 gap-1.5">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="aspect-square bg-black-200 rounded-[2px] overflow-hidden">
          {thumbnails[index] ? (
            // `[DISPLAY_URL_NEVER_OUTBOUND]` — display slot, so it goes through the resize edge.
            // It only rewrites app_cdn urls, which is why the sync copies imported images there:
            // a Stylezone url passes through untouched and a 150px card downloads the full-size
            // original (~260KB each, 96 per folder page ≈ 25MB).
            <img src={previewUrl(thumbnails[index], { w: 256 })} alt="" loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-black-200" />
          )}
        </div>
      ))}
    </div>
  </div>
);

const Integrations = () => {
  const { t } = useTranslation();
  const cardFooter = useIntegrationCardFooter();
  const { user } = useAppSelector((state) => state.common);
  const { isConnected, credentials: stored, folders, syncedAt, isLoading, connect, refresh } = useBrowzwearBoards();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFolder, setSelectedFolder] = useState<IBrowzwearFolder | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<IBrowzwearBoard | null>(null);
  const [page, setPage] = useState(1);
  const [popupImage, setPopupImage] = useState<{ image: string } | null>(null);
  const [credentials, setCredentials] = useState<BrowzwearCredentials>({
    apiKey: stored.apiKey,
    apiSecret: stored.apiSecret,
    storeName: stored.storeName,
    email: stored.email || user.email || "",
  });

  // `connect` commits to Redux itself, which flips `isConnected` and swaps this form for the board
  // browser below. The old local `handleConnect` fetched the boards and then never dispatched, so the
  // form re-rendered over its own successful import — the reported "nothing is listed" (REF-6494).
  const handleConnect = useCallback(() => {
    void connect(credentials);
  }, [connect, credentials]);

  useEffect(() => {
    if (isConnected) {
      void refresh();
    }
    // Mount-only on purpose: `refresh` is a new identity whenever the stored credentials change, so
    // listing it here would re-import in a loop. (`react-hooks/exhaustive-deps` is not wired up in
    // this repo's `.eslintrc.js`, so nothing flags either choice — hence the note.)
  }, []);

  /** Collections first, then the rest. A Collection is a GROUP whose `group_type` is `collection` —
   * there is no collection object to fetch, and flattening the folder level away (which every
   * consumer used to do) is exactly what made them invisible. */
  const visibleFolders = useMemo(() => sortCollectionsFirst(filterFolders(folders, searchQuery)), [folders, searchQuery]);

  const openFolder = useCallback((folder: IBrowzwearFolder) => {
    setSelectedFolder(folder);
    setPage(1);
    // A folder with no real boards is returned with ONE synthesised board carrying the group's own
    // id (`is_virtual`). There is no board level to browse there, so skip straight to its images
    // rather than making the user click through a card that repeats the folder's name.
    const boards = folder.boards ?? [];
    setSelectedBoard(boards.length === 1 && boards[0].is_virtual ? boards[0] : null);
  }, []);

  const goToFolders = useCallback(() => {
    setSelectedFolder(null);
    setSelectedBoard(null);
    setPage(1);
  }, []);

  const goToBoards = useCallback(() => {
    setSelectedBoard(null);
    setPage(1);
  }, []);

  // Inside the shared card the button goes to the card footer (the same place and look as the Account placement's); standalone it stays below the form.
  const connectButton = (
    <Button className="btn-primary-small" onClick={handleConnect} disabled={!credentials.apiKey || !credentials.apiSecret || !credentials.storeName || isLoading}>
      {isLoading ? t("assets:assets.integrations.connecting") : t("assets:assets.integrations.connect_button")}
    </Button>
  );

  if (!isConnected) {
    return (
      <div className="flex flex-col gap-4 max-w-md mx-auto mt-10 p-5 bg-black-200 dark:bg-black-200 rounded-md">
        <BrowzwearAPIForm credentials={credentials} setCredentials={setCredentials} />
        {cardFooter ? createPortal(connectButton, cardFooter) : <div className="flex justify-end">{connectButton}</div>}
      </div>
    );
  }

  // ── Level 3: one board's images ────────────────────────────────────────
  if (selectedBoard) {
    const urls = boardImageUrls(selectedBoard);
    const total = pageCount(urls.length);

    return (
      <div className="flex flex-col gap-4 mt-3">
        {popupImage && <ImagePopup show image={popupImage.image} onClose={() => setPopupImage(null)} />}
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 text-trueBlack dark:text-white hover:text-grey transition-colors" onClick={selectedBoard.is_virtual ? goToFolders : goToBoards}>
            <IconBack className="w-4 h-4" />
            <span className="text-head7">{selectedBoard.board_name}</span>
          </button>
          {urls.length > 0 && <span className="text-grey text-bodySm">{t("assets:assets.integrations.image_count", { count: urls.length })}</span>}
        </div>
        {urls.length === 0 ? (
          <div className="flex-center flex-col gap-2 py-8 px-4 text-center">
            <p className="text-grey text-bodyMedium">{t("assets:assets.integrations.board_empty")}</p>
          </div>
        ) : (
          <>
            <div className={GRID}>
              {pageOf(urls, page).map((url, index) => (
                <div key={`${url}-${index}`} className="cursor-pointer relative w-full rounded-[3px] aspect-square group overflow-hidden">
                  <img src={previewUrl(url, { w: 512 })} alt="" loading="lazy" className="w-full h-full rounded-[3px] object-cover" />
                  <button
                    className="absolute top-1 right-1 w-6 h-6 bg-trueWhite rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPopupImage({ image: url });
                    }}
                  >
                    <IconZoom className="w-4 h-4 text-trueBlack" />
                  </button>
                </div>
              ))}
            </div>
            <Pager page={page} total={total} onChange={setPage} />
          </>
        )}
      </div>
    );
  }

  // ── Level 2: one folder's boards ───────────────────────────────────────
  if (selectedFolder) {
    const boards = selectedFolder.boards ?? [];
    const total = pageCount(boards.length);

    return (
      <div className="flex flex-col gap-4 mt-3">
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 text-trueBlack dark:text-white hover:text-grey transition-colors" onClick={goToFolders}>
            <IconBack className="w-4 h-4" />
            <span className="text-head7">{selectedFolder.group_name}</span>
          </button>
          {isCollection(selectedFolder) && <span className="px-1.5 py-0.5 rounded bg-purpleGreen/20 text-purpleGreen text-bodyXs">{t("assets:assets.integrations.collection_badge")}</span>}
        </div>
        {boards.length === 0 ? (
          <div className="flex-center flex-col gap-2 py-8 px-4 text-center">
            <p className="text-grey text-bodyMedium">{t("assets:assets.integrations.folder_empty")}</p>
          </div>
        ) : (
          <>
            <div className={GRID}>
              {pageOf(boards, page).map((board) => (
                <TreeCard
                  key={board.board_id}
                  name={board.board_name}
                  subtitle={t("assets:assets.integrations.image_count", { count: boardImageUrls(board).length })}
                  thumbnails={boardImageUrls(board).slice(0, 4)}
                  onClick={() => {
                    setSelectedBoard(board);
                    setPage(1);
                  }}
                />
              ))}
            </div>
            <Pager page={page} total={total} onChange={setPage} />
          </>
        )}
      </div>
    );
  }

  // ── Level 1: collections + folders ─────────────────────────────────────
  const total = pageCount(visibleFolders.length);

  return (
    <div className="flex flex-col gap-4 mt-3">
      <div className="flex items-center gap-2">
        {/* the toolbar's search and an icon button, as every page's (2026-10-26) */}
        <FilterSearch
          onSearch={(query: string) => {
            setSearchQuery(query);
            setPage(1);
          }}
          className="max-w-[280px]"
          searchPeriod={500}
        />
        <SleekToggle
          // `true` = re-walk Stylezone (`/sync`). Opening the page reads the mirror; this is the
          // only call that goes upstream, and the user asked for it.
          onClick={() => void refresh(true)}
          disabled={isLoading}
          title={t("assets:assets.integrations.refresh")}
          aria-label={t("assets:assets.integrations.refresh") as string}
          icon={<IconRefresh className={clsx("h-4 w-4", isLoading && "animate-spin")} />}
        />
        {syncedAt && !isLoading && <span className="text-grey text-bodyXs">{t("assets:assets.integrations.synced_at", { time: new Date(syncedAt).toLocaleString() })}</span>}
      </div>
      {isLoading && folders.length === 0 ? (
        <div className={GRID}>
          {Array.from({ length: PAGE_SIZE / 3 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="flex flex-col gap-2 border border-black-300 rounded-md p-3 animate-pulse">
              <div className="h-4 w-1/2 bg-black-200 rounded" />
              <div className="grid grid-cols-2 gap-1.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="aspect-square bg-black-200 rounded-[2px]" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : visibleFolders.length === 0 ? (
        <div className="flex-center flex-col gap-2 py-8 px-4 text-center">
          <p className="text-grey text-bodyMedium">{t("assets:assets.integrations.no_boards")}</p>
        </div>
      ) : (
        <>
          <div className={GRID}>
            {pageOf(visibleFolders, page).map((folder) => (
              <TreeCard
                key={folder.group_id}
                name={folder.group_name}
                badge={isCollection(folder) ? t("assets:assets.integrations.collection_badge") : undefined}
                subtitle={t("assets:assets.integrations.board_count", { count: (folder.boards ?? []).length })}
                thumbnails={folderImageUrls(folder).slice(0, 4)}
                onClick={() => openFolder(folder)}
              />
            ))}
          </div>
          <Pager page={page} total={total} onChange={setPage} />
        </>
      )}
    </div>
  );
};

export default Integrations;
